import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Heart, Shield, BookMarked, Globe, Users, Target, 
  BookOpen, Radio, GraduationCap, Store, ArrowRight,
  CheckCircle2, Sparkles
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: BookMarked,
      title: 'Spiritual Learning',
      description: 'Access thousands of Katha, Kirtan, and Gurbani explanations from renowned scholars and teachers.',
      color: 'bg-royal-500'
    },
    {
      icon: Globe,
      title: 'Global Community',
      description: 'Connect with Sikhs worldwide. Learn, share, and grow together as one united Sangat.',
      color: 'bg-saffron-500'
    },
    {
      icon: Shield,
      title: 'Authentic Content',
      description: 'All content is carefully curated to ensure authenticity and respect for Sikh traditions.',
      color: 'bg-green-600'
    },
    {
      icon: Heart,
      title: 'Seva Spirit',
      description: "Built with love and devotion to serve the Panth and spread Guru's eternal message.",
      color: 'bg-purple-600'
    }
  ];

  const features = [
    { icon: Radio, text: 'Live Kirtan from historic Gurudwaras worldwide' },
    { icon: BookOpen, text: 'Extensive Katha library from renowned speakers' },
    { icon: GraduationCap, text: 'Online courses for Gurmukhi and Sikh studies' },
    { icon: Users, text: 'Connect with experienced Sikh teachers' },
    { icon: Store, text: 'Shop authentic Sikh accessories - Dastar, Kirpan, Kara, Kanga & more' },
    { icon: Sparkles, text: 'AI-powered assistant for Sikhism learning' },
  ];

  return (
    <div className="min-h-screen bg-cream-100" data-testid="about-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-saffron-200 mb-6">
              <Heart className="w-4 h-4 text-saffron-600" />
              <span className="text-sm font-medium text-gray-700">Our Mission & Purpose</span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-royal-700 mb-6">
              About <span className="text-saffron-500">Khalsa Sidak</span>
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              A dedicated digital platform and Sikh accessories shop designed to support the Sikh community 
              with learning, guidance, spiritual updates, and authentic religious items.
            </p>
          </div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1614252371231-f6c9b1f99fe9?w=800&auto=format&fit=crop"
                alt="Golden Temple"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-royal-700">
                What is Khalsa Sidak?
              </h2>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="text-lg">
                  <strong className="text-royal-700">Khalsa Sidak</strong> is a comprehensive digital platform 
                  and <strong className="text-saffron-600">Sikh accessories shop</strong> created to serve the global Sikh community. 
                  Our mission is to make Sikh teachings, traditions, community resources, and authentic religious items 
                  accessible to everyone, everywhere.
                </p>
                
                <p>
                  We help users stay connected with Sikh values, education, and community initiatives 
                  in an easy and accessible way. Whether you're a lifelong Sikh seeking deeper knowledge, 
                  looking for authentic <strong className="text-royal-700">Dastar, Kirpan, Kara, or Kanga</strong>, 
                  or someone curious about Sikhism, our platform welcomes you.
                </p>
                
                <p>
                  The app aims to promote <strong className="text-royal-700">knowledge</strong>, 
                  <strong className="text-royal-700"> discipline (Sidak)</strong>, and 
                  <strong className="text-royal-700"> unity</strong> through modern technology, 
                  bridging the gap between ancient wisdom and contemporary accessibility.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-royal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Target className="w-12 h-12 text-saffron-400 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Our Mission
          </h2>
          <p className="text-xl text-royal-100 leading-relaxed mb-4">
            "ਸਰਬੱਤ ਦਾ ਭਲਾ" - Welfare of All
          </p>
          <p className="text-royal-200 max-w-2xl mx-auto">
            Khalsa Sidak is committed to spreading the universal message of Guru Nanak Dev Ji - 
            equality, compassion, and selfless service to humanity. We believe technology can 
            be a powerful tool to unite the Sangat and preserve our rich heritage for future generations.
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-royal-700 mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything we do is guided by the principles of Sikhi and our commitment to the community.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-white border-gray-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-4 text-center">
                  <div className={`w-14 h-14 ${value.color} rounded-xl flex items-center justify-center mx-auto`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-royal-700">
                What We Offer
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Khalsa Sidak brings together all the resources you need to deepen your connection 
                with Sikhi and the global Sikh community.
              </p>
              
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-saffron-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-4 h-4 text-saffron-600" />
                    </div>
                    <p className="text-gray-700">{feature.text}</p>
                  </div>
                ))}
              </div>
              
              <Link to="/collaborate">
                <Button className="btn-primary mt-4">
                  Join Our Community
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="bg-gradient-to-br from-saffron-50 to-royal-50 rounded-2xl p-8">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-saffron-500 to-gold-500 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-white font-heading font-bold text-3xl">ੴ</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-royal-700">
                  Ik Onkar - One God
                </h3>
                <p className="text-gray-600">
                  The fundamental belief of Sikhism that there is only one God, who is the creator 
                  and sustainer of the universe. This universal truth guides our platform's 
                  commitment to serving all of humanity.
                </p>
                <div className="pt-4">
                  <p className="text-saffron-600 font-heading text-lg">
                    ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ
                  </p>
                  <p className="text-saffron-600 font-heading text-lg">
                    ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਿਹ
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-saffron-500 to-saffron-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-saffron-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of Sikhs worldwide who are using Khalsa Sidak to learn, 
            connect, and grow in their spiritual journey.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/live-kirtan">
              <Button className="bg-white text-saffron-600 hover:bg-gray-100 rounded-full px-8 py-3">
                <Radio className="w-5 h-5 mr-2" />
                Watch Live Kirtan
              </Button>
            </Link>
            <Link to="/courses">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3">
                <GraduationCap className="w-5 h-5 mr-2" />
                Start Learning
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
